//#region IMPORT
import { useState, useEffect, useRef, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { v4 as uuidv4 } from 'uuid';
import { COM_BASIC } from './index';
import { Loading } from '../../../../Components/MUIs';
import dayjs from 'dayjs';
import {
  setChanged,
  setDeleted,
  setRowData,
  setSelected,
} from '../../../../Reducer/GridReducer';
import {
  useSearch1,
  useSearch2,
  useSave1,
  useDelete1,
} from './COM_BASIC_QUERY';
import { useSave } from '../../../../hooks/useSaveN';
import useDelete from '../../../../hooks/useDeleteN';
import useActionLog from '../../../../hooks/useActionLog';
import { COM_BASIC_Schema } from './COM_BASIC_Schema';
//#endregion

// 화면 MENU_ID
const SCREEN_MENU_ID = 'Company';

// 대표자 설정하기 모달 및 데이터 상태 관리
const ModalState = {
  modal1: { open: false, rows: [], selected: [], changed: [] },
};

function ModalReducer(state, action) {
  switch (action.type) {
    case 'OPEN':
      return { ...state, open: true };
    case 'CLOSE':
      return { ...state, open: false };
    case 'SET_ROWS':
      return { ...state, rows: action.payload };
    case 'SET_SELECTED':
      return { ...state, selected: action.payload };
    case 'SET_CHANGED':
      return { ...state, changed: action.payload };
    default:
      return state;
  }
}

//#region 상수
const COL_WIDTH = ['20%', '30%', '20%', '30%'];
const today = dayjs().format('YYYY-MM-DD');
const Inputs = {
  no: '',
  coCd: '', // 회사코드 (=채번)
  coNm: '', // 회사명
  coId: '', //대표자(사업주)
  name: '', // 대표자(사업주)
  address1: '', // 주소
  address2: '', // 상세주소
  contractStartDate: today, // 계약시작일자
  contractEndDate: today, // 계약종료일자
  contractMemberCount: 0, //총계약인원 수
  monthlyContractFee: '', // 계약금(월), 원 단위
  paymentDay: '', // 매월 결제일(1~31), 없으면 빈값
};

function COM_BASICF() {
  //#region 변수
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);

  const didInitSelect = useRef(false);
  useActionLog(); // useSave 내부 로깅용

  //#region 그리드 변수
  const gridState1 = useSelector((state) => state.grid.grid1);
  const setRowData1 = (rows) =>
    dispatch(setRowData({ gridId: 'grid1', rowData: rows }));
  const setDeletedRowData1 = (rows) =>
    dispatch(setDeleted({ gridId: 'grid1', deleted: rows }));
  const setChangedRowData1 = (rows) =>
    dispatch(setChanged({ gridId: 'grid1', changed: rows }));
  const [inputs, setInputs] = useState(Inputs);
  //#region 대표자 설정하기 변수
  const [modalState, dispatchModal] = useReducer(ModalReducer, ModalState);
  const gridRef1 = useRef(); //회사정보 그리드
  const inputRef = useRef(null); //추가 버튼 시 포커스
  const inputRef2 = useRef(null); //대표자 목록에서 선택 클릭 시 포커스
  const [focusEnabled, setFocusEnabled] = useState(true);

  const gridRef2 = useRef(); //대표자설정하기 팝업 그리드
  const selectedRowNosRef = useRef(new Set());
  const lastCheckedIndexRef = useRef(null);
  const currentRowIndexRef = useRef(null);

  const { refetch: Search1 } = useSearch1();

  useEffect(() => {
    if (didInitSelect.current) return;

    const gridApi = gridRef1?.current?.api;

    if (gridApi && gridState1.rowData.length > 0) {
      gridApi.deselectAll(); // 기존 선택 초기화
      gridApi.ensureIndexVisible(0, 'middle');
      gridApi.setFocusedCell(0, 'coNm');

      const firstRowNode = gridApi.getDisplayedRowAtIndex(0);
      const firstRowData = firstRowNode?.data;

      if (firstRowData) {
        const copied = JSON.parse(JSON.stringify(firstRowData));
        handleRowSelection(copied, 0);
        firstRowNode.setSelected(true, false);
        didInitSelect.current = true; // 다시 실행되지 않도록 설정
      }
    }
  }, [gridState1.rowData]);

  const { run: Search2 } = useSearch2();
  const delete1Mutation = useDelete1();
  const deleteHandler1 = useDelete({
    gridRef: gridRef1,
    gridState: gridState1,
    deleteMutation: delete1Mutation,
    updateDeleted: setDeletedRowData1,
    updateRowData: setRowData1,
    rowData: gridState1.rowData,
    setChangedRowData: setChangedRowData1,
  });

  const ResetLogic = () => {
    setInputs(Inputs);
    dispatch(setSelected({ gridId: 'grid1', selected: [] }));
    dispatch(setChanged({ gridId: 'grid1', changed: [] }));
    dispatch(setDeleted({ gridId: 'grid1', deleted: [] }));
  };

  const { runAsync: Save1Async } = useSave1();
  const { run: Save1 } = useSave({
    menuId: SCREEN_MENU_ID,
    requiredLabel: '회사명',
    schema: COM_BASIC_Schema,
    getChangedRows: () =>
      gridState1.changed?.filter((row) => !row.deleteYn) ?? [],
    getDeletedRows: () => gridState1.deleted ?? [],
    requestSave: (rows) => Save1Async(rows),
    requestDelete: (deletedRows) => deleteHandler1.DeleteLogic(deletedRows),
    reset: ResetLogic,
    refresh: Search1,
  });

  // 여러 번 수정 시 같은 'no'값이 changed에 반영되지 않도록 하는 함수
  // 폼이 없는 그리드는 넣어주지 않아도 됨

  const updateCurrentRowData = (fieldName, value, skipValidation = false) => {
    if (!gridRef1.current || currentRowIndexRef.current === null) return;

    const api = gridRef1.current.api;
    const rowNode = api.getDisplayedRowAtIndex(currentRowIndexRef.current);
    if (!rowNode) return;

    const prevData = rowNode.data;
    const updatedRowData = {
      ...prevData,
      [fieldName]: value,
      editYn: 'Y',
    };

    rowNode.setData(updatedRowData);

    if (
      !skipValidation &&
      (!updatedRowData.coNm || !updatedRowData.coNm.trim())
    ) {
      return;
    }

    if (!Array.isArray(gridState1.changed)) return;

    const alreadyChangedIndex = gridState1.changed.findIndex(
      (item) => item.no === updatedRowData.no
    );
    const newChangedList = [...gridState1.changed];

    if (alreadyChangedIndex > -1) {
      newChangedList[alreadyChangedIndex] = updatedRowData;
    } else {
      newChangedList.push(updatedRowData);
    }

    dispatch(
      setChanged({
        gridId: 'grid1',
        changed: newChangedList,
      })
    );
  };

  const tableData = [
    {
      cells: [
        {
          gubun: 'R',
          content: '회사정보',
          colSpan: 4,
        },
      ],
    },
    {
      cells: [
        {
          gubun: 'R',
          content: '회사명',
        },
        {
          gubun: 'I',
          type: 'input',
          value: inputs.coNm ? inputs.coNm : '',
          field: 'coNm',
          isRequired: userInfo.authCd === 'ADMIN' ? true : false, // 필수 입력 지정
          inputRef: inputRef,
          onChange: (e) => {
            const { name, value } = e.target;
            const updatedInputs = { ...inputs, [name]: value, newYn: 'Y' };
            setInputs(updatedInputs);
            updateCurrentRowData(name, value);
          },
          disabled: userInfo.authCd === 'ADMIN' ? false : true,
        },
        {
          gubun: 'R',
          content: '대표자',
        },
        {
          gubun: 'I',
          type: 'input',
          field: 'name',
          value: inputs.coId ? `${inputs.name}/ ${inputs.coId}` : '',
          placeholder: '설정하기',
          onClick: (e) => {
            if (!modalState.open) {
              SearchMember();
            }
          },
          onFocus: (e) => {
            if (!modalState.open && focusEnabled) {
              setFocusEnabled(false);
            }
            if (focusEnabled) {
              SearchMember();
            }
          },
          disabled: userInfo.authCd === 'ADMIN' ? false : true,
        },
      ],
    },
    {
      cells: [
        {
          gubun: 'R',
          content: '주소',
        },
        {
          gubun: 'I',
          type: 'input',
          value: inputs.address1 ? inputs.address1 : '',
          field: 'address1',
          //inputRef: inputRef2,
          onChange: (e) => {
            const { name, value } = e.target;
            const updatedInputs = { ...inputs, [name]: value, newYn: 'Y' };
            setInputs(updatedInputs);
            updateCurrentRowData(name, value);
          },
          disabled: userInfo.authCd === 'ADMIN' ? false : true,
        },
        {
          gubun: 'R',
          content: '상세주소',
        },
        {
          gubun: 'I',
          type: 'input',
          field: 'address2',
          value: inputs.address2 ? inputs.address2 : '',
          onChange: (e) => {
            const { name, value } = e.target;
            const updatedInputs = { ...inputs, [name]: value, newYn: 'Y' };
            setInputs(updatedInputs);
            updateCurrentRowData(name, value);
          },
          disabled: userInfo.authCd === 'ADMIN' ? false : true,
        },
      ],
    },
    {
      cells: [
        {
          gubun: 'R',
          content: '계약시작일자',
        },
        {
          gubun: 'I',
          type: 'datePicker',
          readOnly: userInfo.authCd === 'ADMIN' ? false : true,
          disabled: userInfo.authCd === 'ADMIN' ? false : true,
          defaultValue: inputs.contractStartDate
            ? dayjs(inputs.contractStartDate)
            : dayjs(),
          value: inputs.contractStartDate
            ? dayjs(inputs.contractStartDate)
            : dayjs(),
          onChange: (date) => {
            const changeDate = dayjs(date).format('YYYY-MM-DD');
            if (date) {
              const updatedInputs = {
                ...inputs,
                contractStartDate: changeDate,
                newYn: 'Y',
              };
              setInputs(updatedInputs);
              updateCurrentRowData('contractStartDate', changeDate);
            }
          },
          currentLocale: 'ko',
          format: 'YYYY-MM-DD',
        },
        {
          gubun: 'R',
          content: '계약종료일자',
        },
        {
          gubun: 'I',
          type: 'datePicker',
          readOnly: userInfo.authCd === 'ADMIN' ? false : true,
          disabled: userInfo.authCd === 'ADMIN' ? false : true,
          defaultValue: inputs.contractEndDate
            ? dayjs(inputs.contractEndDate)
            : dayjs(),
          value: inputs.contractEndDate
            ? dayjs(inputs.contractEndDate)
            : dayjs(),
          onChange: (date) => {
            if (date) {
              const changeDate = dayjs(date).format('YYYY-MM-DD');
              const updatedInputs = {
                ...inputs,
                contractEndDate: changeDate,
                newYn: 'Y',
              };
              setInputs(updatedInputs);
              updateCurrentRowData('contractEndDate', changeDate);
            }
          },
        },
      ],
    },
    {
      cells: [
        {
          gubun: 'R',
          content: '총 계약 인원 수',
        },
        {
          gubun: 'I',
          type: 'number',
          value: inputs.contractMemberCount ? inputs.contractMemberCount : '',
          field: 'contractMemberCount', // 필수
          isRequired: userInfo.authCd === 'ADMIN' ? true : false, // 필수 입력 지정
          disabled: userInfo.authCd === 'ADMIN' ? false : true,
          style: {
            color: '#999',
            cursor: 'not-allowed',
          },
          onChange: (e) => {
            const { name, value } = e.target;

            const updatedInputs = {
              ...inputs,
              [name]: value,
              newYn: 'Y',
            };
            setInputs(updatedInputs);
            updateCurrentRowData(name, value);
          },
        },
        {
          gubun: 'R',
          content: '계약금(월)',
        },
        {
          gubun: 'I',
          type: 'number',
          value:
            inputs.monthlyContractFee === '' ||
            inputs.monthlyContractFee === null ||
            inputs.monthlyContractFee === undefined
              ? ''
              : inputs.monthlyContractFee,
          field: 'monthlyContractFee',
          isRequired: userInfo.authCd === 'ADMIN' ? true : false,
          disabled: userInfo.authCd === 'ADMIN' ? false : true,
          inputProps: { min: 0, step: 1 },
          placeholder: '월 단위 금액(원)',
          onChange: (e) => {
            const { name, value } = e.target;
            const next = value === '' ? '' : Number(value);
            const updatedInputs = {
              ...inputs,
              [name]: next,
              newYn: 'Y',
            };
            setInputs(updatedInputs);
            updateCurrentRowData(name, next);
          },
        },
      ],
    },
    {
      cells: [
        {
          gubun: 'R',
          content: '결제일(매월)',
        },
        {
          gubun: 'I',
          type: 'number',
          value:
            inputs.paymentDay === '' ||
            inputs.paymentDay === null ||
            inputs.paymentDay === undefined
              ? ''
              : inputs.paymentDay,
          field: 'paymentDay',
          isRequired: userInfo.authCd === 'ADMIN' ? true : false,
          disabled: userInfo.authCd === 'ADMIN' ? false : true,
          inputProps: { min: 1, max: 31 },
          placeholder: '1~31',
          onChange: (e) => {
            const { name, value } = e.target;
            const next =
              value === '' ? '' : Math.min(31, Math.max(1, Number(value)));
            const updatedInputs = {
              ...inputs,
              [name]: next,
              newYn: 'Y',
            };
            setInputs(updatedInputs);
            updateCurrentRowData(name, next);
          },
        },
        {
          gubun: 'R',
          content: '',
        },
        {
          gubun: 'I',
          type: 'input',
          value: '',
          field: '_paymentRowPad',
          disabled: true,
          onChange: () => {},
        },
      ],
    },
  ];

  //#endregion

  const SearchMember = async () => {
    if (modalState.open) return;
    setLoading(true);
    try {
      await Search2(undefined, {
        onSuccess: (rows) => {
          if (rows?.[0]?.type === 'Error') {
            dispatch(showSnackbar({ message: rows[0].message, type: 'error' }));
            return;
          }
          if (!rows || rows.length === 0) {
            dispatch(
              showSnackbar({ message: '데이터가 없습니다.', type: 'info' })
            );
            dispatchModal({ type: 'SET_ROWS', payload: [] });
          } else {
            dispatchModal({ type: 'SET_ROWS', payload: rows });
            dispatchModal({ type: 'OPEN' });
          }
        },
      });
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  };

  //추가 버튼 클릭 시 이벤트
  const HandleAddClick = async (_gridRef) => {
    dispatch(setSelected({ gridId: 'grid1', selected: [] }));
    if (_gridRef) {
      const api = _gridRef.current.api;
      inputRef.current?.focus();
      api.deselectAll();
      const newRow = {
        coCd: '',
        coNm: '',
        coId: '',
        name: '',
        address1: '',
        address2: '',
        contractStartDate: today,
        contractEndDate: today,
        contractMemberCount: 0,
        monthlyContractFee: '',
        paymentDay: '',
        no: uuidv4(),
        newYn: 'Y',
      };

      _gridRef.current.api.applyTransaction({
        add: [newRow],
        addIndex: 0,
      });

      const addedRowNode = _gridRef.current.api.getDisplayedRowAtIndex(0);
      if (addedRowNode) {
        setInputs(addedRowNode.data);
        currentRowIndexRef.current = 0;
        addedRowNode.setSelected(true);
      }
      ResetLogic();
    }
  };

  const handleRowSelection = (rowData, rowIndex) => {
    setInputs({ ...rowData });
    currentRowIndexRef.current = rowIndex;
    dispatch(setSelected({ gridId: 'grid1', selected: [rowData] }));
  };

  const NewYnErrorMsg = () => {
    dispatch(
      showSnackbar({
        message: '데이터 입력중입니다. 저장 후 시도하세요.',
        type: 'warning',
      })
    );
  };

  //#endregion

  return (
    <div className="wrap">
      <COM_BASIC
        gridId1={'grid1'} // 전역 상태와 그리드 연결해 줄 그리드 아이디
        inputs={inputs}
        rowData={gridState1.rowData}
        setInputs={setInputs}
        modalToggle1={modalState.open}
        setModalToggle1={(open) =>
          dispatchModal({ type: open ? 'OPEN' : 'CLOSE' })
        }
        modalRowData1={modalState.rows}
        selectedModalRowData1={modalState.selected}
        setSelectedModalRowData1={(sel) =>
          dispatchModal({ type: 'SET_SELECTED', payload: sel })
        }
        setSelectedRowData={(sel) =>
          dispatch(setSelected({ gridId: 'grid1', selected: sel }))
        }
        selectedRowData={gridState1.selected}
        colWidth={COL_WIDTH}
        tableData={tableData}
        gridRef1={gridRef1}
        gridState={gridState1}
        dispatchGrid={dispatch}
        inputRef={inputRef}
        inputRef2={inputRef2}
        setFocusEnabled={setFocusEnabled}
        handleRowSelection={handleRowSelection}
        updateCurrentRowData={updateCurrentRowData}
        userInfo={userInfo}
        gridRef2={gridRef2}
        selectedRowNosRef={selectedRowNosRef}
        lastCheckedIndexRef={lastCheckedIndexRef}
        // 함수
        SearchLogic1={Search1}
        SaveLogic1={Save1}
        HandleAddClick={HandleAddClick}
        NewYnErrorMsg={NewYnErrorMsg}
        // MarkedForDeletion={MarkedForDeletion}
        // RestoreRow={RestoreRow}
        MarkedForDeletion={deleteHandler1.MarkedForDeletion}
        RestoreRow={deleteHandler1.RestoreRow}
        DeleteNewRow1={deleteHandler1.DeleteNewRow}
      />
      <Loading open={loading} />
    </div>
  );
}

export default COM_BASICF;
